import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/contexts/AuthContext';
import { ReactErrorBoundary } from '@/components/errors/ReactErrorBoundary';
import { WindowErrorBoundary } from '@/components/errors/WindowErrorBoundary';
import { ScrollToTop } from '@/components/ScrollToTop';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { SuperAdminRoute } from '@/components/SuperAdminRoute';
import { AdminCommandPalette } from '@/components/admin/AdminCommandPalette';
import { LegacyRedirect } from '@/components/admin/LegacyRedirect';
import { LegacyAppRedirect } from '@/components/app/LegacyAppRedirect';
import { AppLayout } from '@/components/layout/AppLayout';
import { useChunkLoadErrorReset } from '@/utils/chunkLoadErrorHandler';

// Critical route - load immediately (landing is the LCP page)
import Landing from '@/pages/Landing';
import { ChatCopilot } from '@/components/ai/ChatCopilot';

// Other public routes - lazy load to shrink initial bundle
const Auth = lazy(() => import('@/pages/Auth'));
const StreamlinedSignup = lazy(() => import('@/pages/StreamlinedSignup'));
const PasswordReset = lazy(() => import('@/pages/PasswordReset'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const UseCasesPage = lazy(() => import('@/pages/UseCasesPage'));
const PublicContact = lazy(() => import('@/pages/PublicContact'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Demo = lazy(() => import('@/pages/Demo').then(m => ({ default: m.Demo })));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const SalesSummaryPDF = lazy(() => import('@/pages/SalesSummaryPDF'));

// Lazy load heavy authenticated routes
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const StreamlinedOnboarding = lazy(() => import('@/pages/StreamlinedOnboarding'));
const RecipeManagement = lazy(() => import('@/pages/RecipeManagement'));
const RecipeEditor = lazy(() => import('@/pages/RecipeEditor'));
const RecipeNutritionLabel = lazy(() => import('@/pages/RecipeNutritionLabel'));
const IngredientManagement = lazy(() => import('@/pages/IngredientManagement'));
const IngredientEditor = lazy(() => import('@/pages/IngredientEditor'));
const MenuPlanning = lazy(() => import('@/pages/MenuPlanning'));
const Menus = lazy(() => import('@/pages/Menus'));
const OrderReview = lazy(() => import('@/pages/OrderReview'));
const Orders = lazy(() => import('@/pages/Orders'));
const OrderConfirmation = lazy(() => import('@/pages/OrderConfirmation'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const Profile = lazy(() => import('@/pages/Profile'));

// New unified admin routes
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const SyscoHub = lazy(() => import('@/pages/admin/SyscoHub'));
const AdminReports = lazy(() => import('@/pages/admin/AdminReports'));
const AdminSupport = lazy(() => import('@/pages/admin/AdminSupport'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));

// Enterprise admin routes
const EnterpriseOverview = lazy(() => import('@/pages/admin/enterprise/EnterpriseOverview'));
const EnterpriseOrganizations = lazy(() => import('@/pages/admin/enterprise/EnterpriseOrganizations'));
const EnterpriseUsers = lazy(() => import('@/pages/admin/enterprise/EnterpriseUsers'));
const EnterpriseProvisioning = lazy(() => import('@/pages/admin/enterprise/EnterpriseProvisioning'));
const EnterpriseSecurity = lazy(() => import('@/pages/admin/enterprise/EnterpriseSecurity'));
const EnterpriseSettings = lazy(() => import('@/pages/admin/enterprise/EnterpriseSettings'));
const EnterpriseAnalyticsJobs = lazy(() => import('@/pages/admin/enterprise/EnterpriseAnalyticsJobs'));
const Subscription = lazy(() => import('@/pages/Subscription'));
const AuthenticatedContact = lazy(() => import('@/pages/AuthenticatedContact'));
const Support = lazy(() => import('@/pages/Support'));
const SupportTicket = lazy(() => import('@/pages/SupportTicket'));
// Inventory merged into IngredientManagement
const Notifications = lazy(() => import('@/pages/Notifications'));
const SyscoCatalog = lazy(() => import('@/features/catalog/components/CatalogPage'));
const BillingManager = lazy(() => import('@/pages/admin/BillingManager'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

function App() {
  // Reset chunk load error flag on successful app load
  useChunkLoadErrorReset();
  
  return (
    <ReactErrorBoundary level="app">
      <AuthProvider>
        <Router>
          <WindowErrorBoundary>
            <AdminCommandPalette />
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes - no lazy loading */}
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/use-cases" element={<UseCasesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/signup" element={<StreamlinedSignup />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/contact" element={<PublicContact />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/sales-summary/:campName" element={<SalesSummaryPDF />} />
              
              {/* New /app/* namespace routes - wrapped in AppLayout */}
              <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
              <Route
                path="/app/dashboard"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <ReactErrorBoundary level="route">
                        <AppLayout>
                          <Dashboard />
                        </AppLayout>
                      </ReactErrorBoundary>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/recipes"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <RecipeManagement />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/recipes/new"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <RecipeEditor />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/recipes/:id/edit"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <RecipeEditor />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/recipes/:id/nutrition-label"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <RecipeNutritionLabel />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/ingredients"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <IngredientManagement />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/ingredients/new"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <IngredientEditor />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/ingredients/:id/edit"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <IngredientEditor />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* Inventory merged into Ingredients - redirect */}
              <Route
                path="/app/inventory"
                element={<Navigate to="/app/ingredients" replace />}
              />
              <Route
                path="/app/menu-planning"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <MenuPlanning />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/menus"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Menus />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/orders"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Orders />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/order-review/:menuId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <OrderReview />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/order-confirmation/:orderId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <OrderConfirmation />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/analytics"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Analytics />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* Sysco Catalog route hidden - backend preserved for allergen functionality */}
              {/* <Route
                path="/app/sysco-catalog"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <SyscoCatalog />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              /> */}
              <Route
                path="/app/notifications"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Notifications />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/profile"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Profile />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/subscription"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Subscription />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/support"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <Support />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/support/ticket/:ticketId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <SupportTicket />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/contact"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AppLayout>
                        <AuthenticatedContact />
                      </AppLayout>
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Legacy app routes - redirect to /app/* namespace */}
              <Route path="/dashboard" element={<LegacyAppRedirect to="/app/dashboard" />} />
              <Route path="/recipes" element={<LegacyAppRedirect to="/app/recipes" />} />
              <Route path="/recipes/new" element={<LegacyAppRedirect to="/app/recipes/new" />} />
              <Route path="/recipes/:id/edit" element={<LegacyAppRedirect to="/app/recipes/:id/edit" />} />
              <Route path="/ingredients" element={<LegacyAppRedirect to="/app/ingredients" />} />
              <Route path="/ingredients/new" element={<LegacyAppRedirect to="/app/ingredients/new" />} />
              <Route path="/ingredients/:id/edit" element={<LegacyAppRedirect to="/app/ingredients/:id/edit" />} />
              <Route path="/inventory" element={<Navigate to="/app/ingredients" replace />} />
              <Route path="/menu-planning" element={<LegacyAppRedirect to="/app/menu-planning" />} />
              <Route path="/order-review" element={<LegacyAppRedirect to="/app/orders" />} />
              <Route path="/order-review/:menuId" element={<LegacyAppRedirect to="/app/order-review/:menuId" />} />
              <Route path="/order-confirmation/:orderId" element={<LegacyAppRedirect to="/app/order-confirmation/:orderId" />} />
              <Route path="/analytics" element={<LegacyAppRedirect to="/app/analytics" />} />
              {/* Sysco Catalog legacy redirect removed - feature hidden from UI */}
              <Route path="/notifications" element={<LegacyAppRedirect to="/app/notifications" />} />
              <Route path="/profile" element={<LegacyAppRedirect to="/app/profile" />} />
              <Route path="/subscription" element={<LegacyAppRedirect to="/app/subscription" />} />
              <Route path="/support" element={<LegacyAppRedirect to="/app/support" />} />
              <Route path="/support/ticket/:ticketId" element={<LegacyAppRedirect to="/app/support/ticket/:ticketId" />} />
              <Route path="/support/contact" element={<LegacyAppRedirect to="/app/contact" />} />
              
              {/* Protected routes - lazy loaded - DEPRECATED: Use /app/* routes above */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <StreamlinedOnboarding />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <ReactErrorBoundary level="route">
                        <Dashboard />
                      </ReactErrorBoundary>
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recipes"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <RecipeManagement />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recipes/new"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <RecipeEditor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recipes/:id/edit"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <RecipeEditor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ingredients"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <IngredientManagement />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ingredients/:id/edit"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <IngredientEditor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* Legacy /inventory route - redirect to merged page */}
              <Route
                path="/inventory"
                element={<Navigate to="/app/ingredients" replace />}
              />
              <Route
                path="/menu-planning"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <MenuPlanning />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-review/:menuId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <OrderReview />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-confirmation/:orderId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <OrderConfirmation />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Analytics />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Profile />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* New unified admin routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <Navigate to="/admin/dashboard" replace />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminUsers />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/sysco"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <SyscoHub />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminReports />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminSupport />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminSettings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              
              {/* Enterprise admin routes (super admin only) */}
              <Route
                path="/admin/enterprise/overview"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseOverview />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/enterprise/organizations"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseOrganizations />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/enterprise/users"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseUsers />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/enterprise/provisioning"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseProvisioning />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/enterprise/security"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseSecurity />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/enterprise/settings"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseSettings />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/analytics-jobs"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseAnalyticsJobs />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />

              {/* Legacy route redirects with toast notifications */}
              <Route 
                path="/admin/sysco-exports" 
                element={
                  <LegacyRedirect 
                    from="/admin/sysco-exports" 
                    to="/admin/sysco?tab=exports" 
                    message="Sysco Exports moved to the Sysco Hub. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/admin/sysco-integration" 
                element={
                  <LegacyRedirect 
                    from="/admin/sysco-integration" 
                    to="/admin/sysco?tab=health" 
                    message="Sysco Integration moved to the Sysco Hub. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/admin/sysco-inbound" 
                element={
                  <LegacyRedirect 
                    from="/admin/sysco-inbound" 
                    to="/admin/sysco?tab=inbound" 
                    message="Sysco Inbound moved to the Sysco Hub. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/admin/sysco-confirmations" 
                element={
                  <LegacyRedirect 
                    from="/admin/sysco-confirmations" 
                    to="/admin/sysco?tab=confirmations" 
                    message="Order Confirmations moved to the Sysco Hub. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/admin/sysco-invoices" 
                element={
                  <LegacyRedirect 
                    from="/admin/sysco-invoices" 
                    to="/admin/sysco?tab=invoices" 
                    message="Invoice Management moved to the Sysco Hub. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/sysco-settings" 
                element={
                  <LegacyRedirect 
                    from="/sysco-settings" 
                    to="/admin/sysco?tab=settings" 
                    message="Sysco Settings moved to the Sysco Hub. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/enterprise-admin" 
                element={
                  <LegacyRedirect 
                    from="/enterprise-admin" 
                    to="/admin/enterprise/overview" 
                    message="Enterprise Admin moved to the Admin Portal. You've been redirected." 
                  />
                } 
              />
              <Route 
                path="/super-admin" 
                element={
                  <LegacyRedirect 
                    from="/super-admin" 
                    to="/admin/enterprise/overview" 
                    message="Super Admin moved to the Admin Portal. You've been redirected." 
                  />
                } 
              />
              <Route
                path="/admin/billing"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Suspense fallback={<PageLoader />}>
                      <BillingManager />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* Legacy Sysco Catalog route removed - feature hidden from UI */}
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Subscription />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support/contact"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AuthenticatedContact />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Notifications />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Support />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support/ticket/:ticketId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <SupportTicket />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              
              {/* Stripe checkout redirects */}
              <Route path="/checkout" element={<Navigate to="/pricing" replace />} />
              <Route path="/subscribe" element={<Navigate to="/pricing" replace />} />
              <Route path="/billing" element={<Navigate to="/pricing" replace />} />
              <Route path="/portal" element={<Navigate to="/contact" replace />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ChatCopilot />
          <Toaster />
          </WindowErrorBoundary>
        </Router>
      </AuthProvider>
    </ReactErrorBoundary>
  );
}

export default App;
